import { useEffect, useMemo, useState } from 'react'
import {
  ArrowLeftRight, ArrowRight, Search, X, Loader2,
  RotateCw, Check, CheckCheck, Trash2, Lock
} from 'lucide-react'
import { apiGet, cachedGet, postJson } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { useEphemeralState } from '../../lib/persist.js'
import { canManage, isAdmin } from '../../lib/role.js'
import { relabelBranch } from '../../lib/ticket.js'
import { rows, todayISO, daysAgoISO, fmtTime, fmtDateTime, stockDefaultBranch } from '../../lib/stock.js'
import {
  STAGES, STAGE_PENDING, STAGE_SENT, STAGE_DONE, stageTone,
  transferQty, transferItemName, creatorCode, modifiedCode, originLevel, destLevel,
} from '../../lib/transfer.js'
import TransferCreateDrawer from '../../components/stock/TransferCreateDrawer.jsx'
import TransferRecordDrawer from '../../components/stock/TransferRecordDrawer.jsx'
import Pager from '../../components/Pager.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

// โอนย้าย — branch-to-branch stock transfers, worked as a 3-stage flow. One tab
// per stage (see lib/transfer.js):
//   1 รออนุมัติ → 2 รอรับของ → 3 เสร็จสิ้น
// Actions live on the tab whose stage they advance FROM:
//   • รออนุมัติ: อนุมัติ (Manager+) → moves to รอรับของ. No stock movement.
//   • รอรับของ: ยืนยันรับของ (anyone) → moves to เสร็จสิ้น AND writes the balanced
//     stock pair (แบ่งให้ out of origin, รับย้าย into destination).
//   • เสร็จสิ้น: read-only log.
// Cancel (ยกเลิก) is available on the first two stages only — once received, the
// transfer owns two stock rows and deleting it would orphan them.

const PER_PAGE = 50

function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = String(iso).split('-')
  if (!y) return iso
  return `${Number(d)}/${Number(m)}/${String(y).slice(2)}`
}

export default function StockTransfer() {
  const me = useMe()
  const mayApprove = canManage(me)
  const myEmpId = me?.employee_id || me?.xano_id || 0

  const [stage, setStage] = useEphemeralState('transfer:stage', STAGE_PENDING)
  const [start, setStart] = useEphemeralState('transfer:start', () => daysAgoISO(30))
  const [end, setEnd] = useEphemeralState('transfer:end', todayISO)
  const [originBranch, setOriginBranch] = useEphemeralState('transfer:origin', '') // เสร็จสิ้น only: filter by ต้นทาง
  const [q, setQ] = useEphemeralState('transfer:q', '')
  const [page, setPage] = useState(1)
  const [active, setActive] = useState(null)   // row open in the view/edit drawer

  const [branches, setBranches] = useState([])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  const [createOpen, setCreateOpen] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [acting, setActing] = useState(false)
  const [toast, setToast] = useState(null)

  const isDone = stage === STAGE_DONE
  const refresh = () => setReloadKey((k) => k + 1)

  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranches(rows(r))).catch(() => {})
  }, [])

  // Switching tab / narrowing the window starts a fresh page and clears any
  // selection carried from the previous view (a bulk action must never span
  // two stages — /step takes one `stage` for the whole list).
  useEffect(() => { setPage(1); setSelected(new Set()) }, [stage, start, end, originBranch])

  useEffect(() => {
    let alive = true
    setLoading(true)
    apiGet('/api/inventory_transfer', {
      stage,
      // Date range + origin-branch filter narrow the finished log only; the open
      // stages show every in-flight transfer regardless of when it was requested.
      start_date: isDone ? (start || undefined) : undefined,
      end_date: isDone ? (end || undefined) : undefined,
      branch_id: isDone ? (originBranch || undefined) : undefined,
      page,
      per_page: PER_PAGE,
    })
      .then((r) => { if (alive) { setData(r); setError(null) } })
      .catch((e) => { if (alive) setError(e.message || 'โหลดรายการโอนไม่สำเร็จ') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [stage, start, end, originBranch, page, reloadKey, isDone])

  const items = useMemo(() => rows(data), [data])

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return items
    return items.filter((r) =>
      transferItemName(r).toLowerCase().includes(needle) ||
      String(r?.inventory?.subCategory || '').toLowerCase().includes(needle) ||
      String(r?.branch_name || '').toLowerCase().includes(needle) ||
      String(r?.branch2_name || '').toLowerCase().includes(needle) ||
      String(r?.remark || '').toLowerCase().includes(needle))
  }, [items, q])

  const total = Number(data?.itemsTotal) || 0
  const pageTotal = Number(data?.pageTotal) || 1

  const canCancelRow = (r) => (stage !== STAGE_DONE) && (mayApprove || Number(r?.created_by) === Number(myEmpId))
  const selectableIds = useMemo(() => (isDone ? [] : visible.map((r) => r.id)), [visible, isDone])
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id))

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(selectableIds))

  // Advance the current stage's selection (or a single id) to the next stage.
  // stage 1 → approve; stage 2 → confirm receipt (which writes the stock pair).
  async function doStep(ids) {
    const list = ids.filter((id) => Number.isInteger(id) && id > 0)
    if (!list.length || acting) return
    setActing(true)
    setError(null)
    try {
      await postJson('/api/inventory_transfer/step', { stage, transfer_list: list })
      setSelected(new Set())
      setToast(stage === STAGE_PENDING
        ? `อนุมัติ ${list.length} ใบโอนแล้ว`
        : `ยืนยันรับของ ${list.length} รายการแล้ว — ตัด/เพิ่มสต๊อกเรียบร้อย`)
      refresh()
    } catch (err) {
      setError(err.message || 'ดำเนินการไม่สำเร็จ')
    } finally {
      setActing(false)
    }
  }

  async function cancel(id) {
    if (acting) return
    setConfirmCancel(null)
    setActing(true)
    setError(null)
    try {
      await deleteJson('/api/inventory_transfer', { inventory_transfer_id: id })
      setSelected((s) => { const n = new Set(s); n.delete(id); return n })
      setToast('ยกเลิกใบโอนแล้ว')
      refresh()
    } catch (err) {
      setError(err.message || 'ยกเลิกไม่สำเร็จ')
    } finally {
      setActing(false)
    }
  }

  const actionLabel = stage === STAGE_PENDING ? 'อนุมัติ' : 'ยืนยันรับของ'
  const ActionIcon = stage === STAGE_PENDING ? Check : CheckCheck
  const canBulk = !isDone && selected.size > 0 && !acting && (stage !== STAGE_PENDING || mayApprove)

  // Edit permission by stage (all rows in a tab share it): ขอโอนย้าย = anyone ·
  // รับของ = Manager+ · เสร็จสิ้น = Admin. The server re-checks against the stored
  // stage, so this only gates the UI. (See functions/api/inventory_transfer.js.)
  const canEditStage = stage === STAGE_PENDING ? true : stage === STAGE_SENT ? mayApprove : isAdmin(me)

  return (
    <div className="px-10 py-8 max-w-[1200px]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight mb-1">โอนย้าย Stock</h1>
          <p className="text-[14px] text-slate-500">โอนของระหว่างสาขา — ขอโอนย้าย → รับของ → เสร็จสิ้น</p>
        </div>
        <button onClick={() => setCreateOpen(true)}
          className="shrink-0 rounded-xl bg-brand-600 px-5 py-2.5 text-[14.5px] font-semibold text-white hover:bg-brand-700 flex items-center gap-2">
          <ArrowLeftRight size={16} />สร้างใบโอน
        </button>
      </div>

      {/* Stage tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 mb-4">
        {STAGES.map((s) => (
          <button key={s.stage} onClick={() => setStage(s.stage)}
            className={`relative px-4 py-2.5 text-[14px] font-medium -mb-px border-b-2 transition ${
              stage === s.stage
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {s.label}
            {stage === s.stage && total > 0 && (
              <span className="ml-2 rounded-full bg-brand-100 text-brand-700 px-2 py-0.5 text-[11.5px] tabular-nums">{total}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {isDone && (
          <>
            <label className="flex items-center gap-2 text-[13px] text-slate-600">
              <span className="text-slate-400">จาก</span>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800" />
            </label>
            <label className="flex items-center gap-2 text-[13px] text-slate-600">
              <span className="text-slate-400">ถึง</span>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800" />
            </label>
            <div className="flex gap-1.5">
              <QuickRange label="7 วัน" onClick={() => { setStart(daysAgoISO(7)); setEnd(todayISO()) }} />
              <QuickRange label="30 วัน" onClick={() => { setStart(daysAgoISO(30)); setEnd(todayISO()) }} />
              <QuickRange label="90 วัน" onClick={() => { setStart(daysAgoISO(90)); setEnd(todayISO()) }} />
            </div>
            <label className="flex items-center gap-2 text-[13px] text-slate-600">
              <span className="text-slate-400">ต้นทาง</span>
              <select value={originBranch} onChange={(e) => setOriginBranch(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800">
                <option value="">ทุกสาขา</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{relabelBranch(b.name || b.Name) || `สาขา ${b.id}`}</option>
                ))}
              </select>
            </label>
          </>
        )}
        <div className="relative flex-1 min-w-[220px] max-w-[380px]">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="กรองในหน้านี้… (ชื่อของ · สาขา · หมายเหตุ)"
            className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-9 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 focus:border-brand-300 focus:ring-2 focus:ring-brand-100 outline-none" />
          {q && (
            <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={15} />
            </button>
          )}
        </div>
        <button onClick={refresh} title="โหลดใหม่"
          className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:text-brand-600 hover:border-brand-300">
          <RotateCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{error}</div>
      )}

      {/* Bulk action bar (open stages only) */}
      {!isDone && (
        <div className="flex items-center gap-3 mb-3 min-h-[36px]">
          {selected.size > 0 ? (
            <>
              <span className="text-[13px] text-slate-500">เลือก {selected.size} รายการ</span>
              <button onClick={() => doStep([...selected])} disabled={!canBulk}
                className="rounded-lg bg-brand-600 px-4 py-1.5 text-[13.5px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 flex items-center gap-1.5">
                {acting ? <Loader2 size={14} className="animate-spin" /> : <ActionIcon size={14} />}
                {actionLabel}ที่เลือก
              </button>
              <button onClick={() => setSelected(new Set())} className="text-[13px] text-slate-400 hover:text-slate-600">ล้างการเลือก</button>
              {stage === STAGE_PENDING && !mayApprove && (
                <span className="text-[12.5px] text-amber-600">อนุมัติได้เฉพาะผู้จัดการ</span>
              )}
            </>
          ) : (
            <span className="text-[12.5px] text-slate-400">
              {stage === STAGE_PENDING
                ? (mayApprove ? 'เลือกใบโอนเพื่ออนุมัติ (ครั้งละหลายรายการได้)' : 'รอผู้จัดการอนุมัติ')
                : 'เลือกรายการที่ได้รับของแล้วเพื่อยืนยัน — ระบบจะตัด/เพิ่มสต๊อกให้อัตโนมัติ'}
            </span>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] font-medium uppercase tracking-wide text-slate-400 border-b border-slate-100">
                {!isDone && (
                  <th className="pl-5 pr-2 py-3 w-8">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll}
                      disabled={!selectableIds.length} className="accent-brand-600" />
                  </th>
                )}
                <th className="px-4 py-3 font-medium">วันที่</th>
                <th className="px-4 py-3 font-medium min-w-[220px]">ของ</th>
                <th className="px-4 py-3 font-medium">เส้นทาง <span className="normal-case font-normal text-slate-300">· คงเหลือ</span></th>
                <th className="px-4 py-3 font-medium text-right">จำนวน</th>
                <th className="px-4 py-3 font-medium">ผู้ทำรายการ</th>
                <th className="px-4 py-3 font-medium text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={isDone ? 6 : 7} className="px-5 py-16 text-center text-slate-400">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…
                </td></tr>
              )}
              {!loading && !visible.length && (
                <tr><td colSpan={isDone ? 6 : 7} className="px-5 py-16 text-center text-slate-400">
                  {q ? `ไม่พบรายการที่ตรงกับ “${q}” ในหน้านี้` : 'ไม่มีรายการในสถานะนี้'}
                </td></tr>
              )}
              {!loading && visible.map((r) => {
                const qty = transferQty(r)
                const oLvl = originLevel(r), dLvl = destLevel(r)
                const cancelable = canCancelRow(r)
                return (
                  <tr key={r.id} onClick={() => setActive(r)}
                    className={`cursor-pointer hover:bg-slate-50/60 ${selected.has(r.id) ? 'bg-brand-50/40' : ''}`}>
                    {!isDone && (
                      <td className="pl-5 pr-2 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="accent-brand-600" />
                      </td>
                    )}
                    <td className="px-4 py-3 align-top whitespace-nowrap text-slate-500 tabular-nums">
                      <div>{fmtDate(r.date)}</div>
                      <div className="text-[11px] text-slate-400">{fmtTime(r.created_at)}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-slate-800">{transferItemName(r)}</div>
                      {r?.inventory?.subCategory && (
                        <div className="text-[11.5px] text-slate-400">{r.inventory.subCategory}</div>
                      )}
                      {r?.remark && <div className="text-[11.5px] text-slate-400 italic mt-0.5">“{r.remark}”</div>}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5 text-slate-700">
                        <BranchDot color={r.branch_color} />{relabelBranch(r.branch_name) || `#${r.branch_id}`}
                        <ArrowRight size={13} className="text-slate-300" />
                        <BranchDot color={r.branch2_color} />{relabelBranch(r.branch2_name) || `#${r.branch2_id}`}
                      </div>
                      <div className="text-[11.5px] text-slate-400 tabular-nums mt-0.5"
                           title="คงเหลือปัจจุบันของของชิ้นนี้ที่สาขาต้นทาง → ปลายทาง">
                        คงเหลือ {oLvl == null ? '—' : oLvl} <span className="text-slate-300">→</span> {dLvl == null ? '—' : dLvl}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">
                      {qty}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap text-slate-500">
                      <div>{creatorCode(r) || '—'}</div>
                      {isDone && (
                        <div className="text-[11px] text-slate-400" title="ผู้ยืนยันรับของ · เวลาที่ยืนยัน">
                          รับ: {modifiedCode(r) || '—'} · {fmtDateTime(r.modified_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {isDone ? (
                        <span className={`inline-block rounded-full border px-2 py-0.5 text-[11.5px] font-medium ${stageTone(STAGE_DONE)}`}>เสร็จสิ้น</span>
                      ) : (
                        <div className="inline-flex items-center gap-1.5">
                          <button onClick={() => doStep([r.id])}
                            disabled={acting || (stage === STAGE_PENDING && !mayApprove)}
                            className="rounded-lg border border-brand-200 bg-brand-50 text-brand-700 px-2.5 py-1 text-[12.5px] font-medium hover:bg-brand-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1">
                            <ActionIcon size={13} />{actionLabel}
                          </button>
                          {cancelable ? (
                            <button onClick={() => setConfirmCancel(r.id)} disabled={acting} title="ยกเลิกใบโอน"
                              className="rounded-lg border border-slate-200 text-slate-400 p-1.5 hover:text-rose-600 hover:border-rose-200 disabled:opacity-40">
                              <Trash2 size={13} />
                            </button>
                          ) : (
                            <span className="p-1.5 text-slate-200" title="ยกเลิกได้เฉพาะผู้สร้างหรือผู้จัดการ"><Lock size={13} /></span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-slate-100 px-5 py-3 text-[13px] text-slate-500">
          <div>
            {q
              ? <>แสดง <b className="text-slate-700">{visible.length}</b> จาก {items.length} รายการในหน้านี้ · ทั้งหมด {total}</>
              : <>ทั้งหมด <b className="text-slate-700">{total}</b> รายการ</>}
          </div>
          <Pager page={page} pageTotal={pageTotal} onChange={setPage} disabled={loading} />
        </div>
      </div>

      {createOpen && (
        <TransferCreateDrawer
          branches={branches}
          defaultBranchId={stockDefaultBranch(null, me?.branch_id)}
          onClose={() => setCreateOpen(false)}
          onSaved={(n) => {
            setCreateOpen(false)
            setToast(`สร้างใบโอน ${n} รายการแล้ว — ขอโอนย้าย`)
            setStage(STAGE_PENDING); setPage(1)
            refresh()
          }}
        />
      )}

      {active && (
        <TransferRecordDrawer
          row={active}
          stage={stage}
          canEdit={canEditStage}
          onClose={() => setActive(null)}
          onChanged={() => { setToast('บันทึกการแก้ไขแล้ว'); refresh() }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-xl bg-slate-900 text-white px-5 py-3 text-[14px] flex items-center gap-3 shadow-lg z-40">
          {toast}
          <button onClick={() => setToast(null)} className="text-slate-400 hover:text-white"><X size={15} /></button>
        </div>
      )}

      {confirmCancel != null && (
        <ConfirmDialog
          title="ยกเลิกใบโอนนี้?"
          message="ใบโอนจะถูกยกเลิกและของจะไม่ถูกโอนออกจากสาขาต้นทาง"
          confirmLabel="ยกเลิกใบโอน" cancelLabel="ปิด" tone="danger"
          onConfirm={() => cancel(confirmCancel)}
          onCancel={() => setConfirmCancel(null)}
        />
      )}
    </div>
  )
}

function BranchDot({ color }) {
  if (!color) return null
  return <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
}

function QuickRange({ label, onClick }) {
  return (
    <button onClick={onClick}
      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12.5px] text-slate-600 hover:border-brand-300 hover:text-brand-700">
      {label}
    </button>
  )
}

// DELETE with a JSON body. api.js#apiDelete takes no body, and the proxy reads
// { inventory_transfer_id } from the request body — so send it directly.
async function deleteJson(path, body) {
  const r = await fetch(path, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!r.ok) {
    const text = await r.text().catch(() => '')
    let detail = text
    try { detail = JSON.parse(text)?.detail || detail } catch { /* keep raw */ }
    throw new Error(detail || `HTTP ${r.status}`)
  }
  return r.json().catch(() => ({}))
}
